import { createTheme } from "@mui/material/styles";
import colors from "./colors";

declare module "@mui/material/styles" {
  interface TypographyVariants {
    "about-content": React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    "about-content"?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    "about-content": true;
  }
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#1976d2",
    },
    secondary: {
      main: "#dc004e",
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 1000, 
      lg: 1200,
      xl: 2000,
    },
  },
  typography: {
    fontFamily: '"Gantari", sans-serif',
    h1: {
      fontSize: "3rem",
      fontWeight: 500,
      lineHeight: 1,
      letterSpacing: "0.2em",
      color: "white",
    },
    h2: {
      fontFamily: '"Gantari", sans-serif',
      letterSpacing: "0.1rem",
      fontWeight: 500,
    },
    h3: {
      fontFamily: '"Gantari", sans-serif',
      fontSize: "1.2rem",
      fontWeight: 400,
    },
    h4: {
      fontFamily: '"Gantari", sans-serif',
      fontSize: "1.2rem",
      fontWeight: 300,
    },
    h5: {
      fontFamily: '"Gantari", sans-serif',
      fontSize: "1rem",
      fontWeight: 200,
    },
    h6: {
      fontFamily: '"Gantari", sans-serif',
      fontSize: "0.8rem",
      fontWeight: 200,
    },
    body1: {
      fontFamily: '"Gantari", sans-serif',
      fontSize: "1.2rem",
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: "0.08rem",
    },
    body2: {
      fontFamily: '"Gantari", sans-serif',
      fontSize: "1rem",
      fontWeight: 300,
    },
    subtitle1: {
      fontFamily: '"Gantari", sans-serif',
      fontSize: "0.8rem",
      fontWeight: 200,
    },
  },
  spacing: 8,

  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          paperShadow: "none",
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          disableRipple: true,
          width: "inherit",
          fontFamily: '"Gantari", sans-serif',
          fontSize: "1.2rem",
          fontWeight: 300,
          textTransform: "none",
          color: colors.hoverBrightness,
          padding: 0,
          minWidth: 0,

          "&:hover": {
            backgroundColor: "none",
            textDecoration: "none",
          },
          "&.MuiTouchRipple-root": {
            backgroundColor: "none",
          },
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          width: "100%",
          color: "none",
          borderRadius: 0,
          padding: "0rem 1rem",
          minWidth: 0,
          "&:hover": {
            backgroundColor: "transparent",
          },
        },
      },
    },

    MuiList: {
      styleOverrides: {
        root: {
          width: "100%",
          padding: "0rem 0rem",
          borderRadius: 0,
          "&:hover": {
            backgroundColor: "transparent",
          },
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          width: "201.45px",
          padding: 0,
          borderRadius: 0,
          boxShadow: "none",
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          width: "100%",
          padding: "0rem 1rem",
          fontFamily: '"Gantari", sans-serif',
          fontSize: "1.5rem",
          fontWeight: 200,
          color: "white",
          "&:hover": {
            backgroundColor: "transparent",
          },
          "&.MuiTouchRipple-root": {
            backgroundColor: "none",
          },
        },
      },
    },

    MuiTypography: {
      styleOverrides: {
        h1: {
          fontSize: "3rem",
          "@media (max-width:1200px)": {
            fontSize: "2.6rem",
          },
          "@media (max-width:1000px)": {
            fontSize: "2.2rem",
          },
          "@media (max-width:600px)": {
            fontSize: "1.8rem",
          },
        },
        h2: {
          fontSize: "2rem",
          "@media (max-width:1300px)": {
            fontSize: "1.8rem",
          },
          "@media (max-width:1100px)": {
            fontSize: "1.5rem",
          },
          "@media (max-width:800px)": {
            fontSize: "1rem",
          },
        },
        h5: {
          fontSize: "1.5rem",
          "@media (max-width:1300px)": {
            fontSize: "1.2rem",
          },
          "@media (max-width:1100px)": {
            fontSize: "1rem",
          },
          "@media (max-width:800px)": {
            fontSize: "0.8rem",
          },
        },
        body1: {
          fontSize: "1.2rem",
          "@media (max-width:1300px)": {
            fontSize: "1.2rem",
          },
          "@media (max-width:1100px)": {
              fontSize: "1rem",
          },
          "@media (max-width:800px)": {
            fontSize: "0.8rem",
          },
        },
        body2: {
          fontSize: "1rem",
          "@media (max-width:1300px)": {
            fontSize: "0.8rem",
          },
          "@media (max-width:1100px)": {
              fontSize: "0.8rem",
          },
          "@media (max-width:700px)": {
            fontSize: "0.7rem",
          },
        },
      },
      variants: [
        {
          props: { variant: "about-content" },
          style: {
            fontFamily: '"Gantari", sans-serif',
            fontSize: "1rem",
            fontWeight: 500,
            lineHeight: 1.5,
            "& p": {
              marginBottom: "1.5rem",
            },
            "& ul": {
              listStyle: "none",
              padding: 0,
              marginBottom: "1.5rem",
            },
            "& li": {
              marginBottom: "0.5rem",
              paddingLeft: "1rem",
              position: "relative",
              "&::before": {
                content: '"•"',
                position: "absolute",
                left: 0,
              },
            },
          },
        },
      ],
    },
  },
});

export default theme;
